/**
 * gate — the consent machine, factored once for every generating surface (plan
 * thinking/plans/2026_09_01_tos_gate.md §8, the browser-flag-only ruling). No React, no imports,
 * no server round-trip: `idle -> asked -> ticked -> accepted`, and `accepted` is absorbing.
 * `useConsentGate.ts` is the only caller; this file is the guarantee layer the ruling's own
 * sentences need — see `__tests__/gate.spec.ts`.
 *
 * `checked` is not a separate field: the 'ticked' status IS the tick. A `GateState` therefore
 * fully describes the card (open or not, ticked or not) from `status` alone; `hasPending` says only
 * whether a press is waiting on this state to resolve, which `useConsentGate` reads to decide
 * whether a stashed closure exists to run.
 *
 * `pressOutcome` never fires anything itself — it is a pure classification of what a `PRESS` should
 * do, so `useConsentGate.ts` can act on it (call the given closure, stash it, or ignore it) without
 * this file needing to know closures exist at all.
 *
 * There is no `firing` status. An earlier revision of this file had one, entered by `PRESS` on a
 * `ticked` state and left by `ACCEPTED` — modelled on §2's original design, where the accepting
 * press awaited a server stamp and `firing` was the state that press was in flight. The
 * browser-flag ruling (§8) writes `localStorage` synchronously: there is no round-trip to hold a
 * state open for, so the hook never actually dispatches `PRESS` for a `ticked` state — it goes
 * straight from computing `accept_then_fire` to dispatching `ACCEPTED`. The status was dead code;
 * removed rather than wired through, the smaller of the two corrections (see the session report
 * for 2026-09-01's fix round).
 */

export type GateStatus = 'idle' | 'asked' | 'ticked' | 'accepted'

export interface GateState {
    status: GateStatus
    hasPending: boolean
}

export type GateEvent =
    | {type: 'PRESS'; accepted: boolean}
    | {type: 'TICK'; checked: boolean}
    | {type: 'ACCEPTED'} // the flag is written and the pending press has run
    | {type: 'FOLD'} // Escape / the surface closed the card; the tick survives

export type PressOutcome = 'fire' | 'ask' | 'focus_checkbox' | 'accept_then_fire'

/** What a `PRESS` should do, given the state it lands on and whether the surface already knows the
 *  visitor accepted (the browser flag, OR'd with this mount having reached `accepted` already). An
 *  accepted visitor short-circuits from ANY status — `accepted` is absorbing, so a stale `idle`/
 *  `asked` read against a flag that is actually `true` (another tab ticked it) still fires. */
export function pressOutcome(s: GateState, accepted: boolean): PressOutcome {
    if (accepted || s.status === 'accepted') return 'fire'
    switch (s.status) {
        case 'idle':
            return 'ask'
        case 'asked':
            return 'focus_checkbox'
        case 'ticked':
            return 'accept_then_fire'
        default:
            return 'ask'
    }
}

export function gateReducer(s: GateState, e: GateEvent): GateState {
    switch (e.type) {
        case 'PRESS': {
            const outcome = pressOutcome(s, e.accepted)
            if (outcome === 'ask') return {status: 'asked', hasPending: true}
            // 'fire' never touched the machine; 'focus_checkbox' is a no-op nudge; 'accept_then_fire'
            // is entered only via a direct `ACCEPTED` dispatch from the hook — the browser-flag
            // write is synchronous, so there is no in-between state for a `PRESS` to hold here.
            return s
        }
        case 'TICK':
            if (s.status !== 'asked' && s.status !== 'ticked') return s
            return {status: e.checked ? 'ticked' : 'asked', hasPending: s.hasPending}
        case 'ACCEPTED':
            return {status: 'accepted', hasPending: false}
        case 'FOLD':
            // Hides the card (the hook's own `folded` flag, outside this state); the tick survives
            // because nothing here changes.
            return s
        default:
            return s
    }
}
